/**
 * Attacker lookup (website roadmap S36 — SR Tools' attacker lookup): one fight from an attack log link, code or id —
 * both players, the weapons, ammo and bonuses used, damage per hit and totals. Pure: the website reads Torn's API attack
 * log (v2 `torn/attacklog?log=<code>`, Public key) from the viewer's browser with their own key; failing that, the
 * Omni-Torn server answers from what it already holds (GET /api/loadouts/fight — the viewer's own kept fights, and the
 * player database's fights the viewer was in). Omni-Torn never reads Torn's attack log pages (loader.php/page.php).
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => {
  const v = typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x.trim()) ? Number(x) : x;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};
const str = (x: unknown, max = 200): string | null => (typeof x === 'string' && x.trim() ? x.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, max) : null);
const posId = (x: unknown): number | null => {
  const n = num(x);
  return n !== null && Number.isInteger(n) && n > 0 && n < 1e9 ? n : null;
};

/** What was pasted: Torn's attack log code (letters and digits) or a fight's id (digits). */
export type AttackRef = { code: string } | { id: number };

const CODE_RE = /^[A-Za-z0-9]{6,64}$/;

/**
 * An attack log link ("…torn.com/page.php?sid=attackLog&ID=af57…", the old loader.php one too), a code, or a fight's id →
 * what to look up; null when it's none of those. Only read from the text — nothing is fetched.
 */
export function attackRefFrom(input: string): AttackRef | null {
  const t = (input ?? '').trim().slice(0, 400);
  if (!t) return null;
  const inLink = /[?&]ID=([A-Za-z0-9]{6,64})(?:[&#\s]|$)/i.exec(t);
  if (inLink && /attackLog/i.test(t)) return { code: inLink[1]! };
  if (/^\d{1,12}$/.test(t)) {
    const id = Number(t);
    return id > 0 ? { id } : null;
  }
  return CODE_RE.test(t) ? { code: t } : null;
}

export type LookupSource = 'api' | 'yours' | 'db';
export type HitKind = 'hit' | 'crit' | 'miss' | 'other';
export type FightSideName = 'attacker' | 'defender';

export interface LookupPlayer {
  /** Null: Torn didn't say (a stealthed attacker). */
  id: number | null;
  name: string;
}

export interface LookupHit {
  /** Torn's time (unix seconds). */
  at: number | null;
  side: FightSideName | null;
  kind: HitKind;
  weapon: string | null;
  ammo: string | null;
  rounds: number | null;
  zone: string | null;
  damage: number | null;
  bonuses: string[];
  /** The line: Torn's own words (API attack log) or built from the turn's facts (the player database). */
  text: string;
}

export interface LookupTotals {
  hits: number;
  crits: number;
  misses: number;
  damage: number;
}

export interface LookupWeapon {
  side: FightSideName | null;
  name: string;
  ammo: string[];
  bonuses: string[];
  hits: number;
  crits: number;
  misses: number;
  damage: number;
}

export interface AttackLookup {
  code: string | null;
  attackId: number | null;
  sources: LookupSource[];
  attacker: LookupPlayer | null;
  defender: LookupPlayer | null;
  started: number | null;
  ended: number | null;
  /** Torn's result word (Hospitalized, Mugged, Lost…) where a source says it. */
  result: string | null;
  hits: LookupHit[];
  weapons: LookupWeapon[];
  totals: Record<FightSideName, LookupTotals>;
}

/** GET /api/loadouts/fight?code=|id= — what the Omni-Torn server already holds of that fight for the viewer. */
export interface AttackFightAnswer {
  /** Merged (mergeLookups), or null when nothing is held. */
  fight: AttackLookup | null;
}

/** Torn's weapon bonus names (as they show in fights). */
export const WEAPON_BONUS_NAMES: readonly string[] = [
  'Achilles', 'Assassinate', 'Backstab', 'Berserk', 'Bleed', 'Blindfire', 'Blindside', 'Bloodlust', 'Burn', 'Comeback', 'Conserve', 'Cripple', 'Crusher', 'Cupid',
  'Deadeye', 'Deadly', 'Demoralize', 'Disarm', 'Double-edged', 'Double Tap', 'Emasculate', 'Empower', 'Eviscerate', 'Execute', 'Expose', 'Finale', 'Focus',
  'Freeze', 'Frenzy', 'Fury', 'Grace', 'Hazardous', 'Home Run', 'Irradiate', 'Lacerate', 'Motivation', 'Paralyze', 'Parry', 'Penetrate', 'Plunder', 'Poison',
  'Powerful', 'Proficience', 'Puncture', 'Quicken', 'Rage', 'Revitalize', 'Roshambo', 'Shock', 'Sleep', 'Slow', 'Smash', 'Smurf', 'Specialist', 'Spray',
  'Storage', 'Stricken', 'Stun', 'Suppress', 'Sure Shot', 'Throttle', 'Toxin', 'Warlord', 'Weaken', 'Wind-up', 'Wither',
];
const BONUS_RE = new RegExp(`\\b(${[...WEAPON_BONUS_NAMES].sort((a, b) => b.length - a.length).map((b) => b.replace(/[-\s]/g, '[-\\s]?')).join('|')})(?:ed|d|s)?\\b`, 'gi');
const BONUS_BY_KEY = new Map(WEAPON_BONUS_NAMES.map((b) => [b.toLowerCase().replace(/[-\s]/g, ''), b]));

/** The weapon bonuses a line names (Torn's spelling, each once). */
export function bonusesIn(text: string): string[] {
  const out = new Set<string>();
  for (const m of (text ?? '').matchAll(BONUS_RE)) {
    const b = BONUS_BY_KEY.get(m[1]!.toLowerCase().replace(/[-\s]/g, ''));
    if (b) out.add(b);
  }
  return [...out];
}

const FIRED = /\bfired (\d+) (?:(.+?) )?rounds? of (?:his|her|their|its) (.+?) (?:hitting|missing)\b/i;
const MELEE = /\bwith (?:his|her|their|its) (.+?) (?:in|for|missing)\b/i;
const ZONE = /\bin the (.+?)(?: for\b|$)/i;
const DAMAGE = /\bfor ([\d,]+)\b(?!.*\bfor [\d,]+\b)/i;

/** One line's kind from Torn's action word ("Hit", "CriticalHit", "Missed"…), else from its words. */
function kindOf(action: string, text: string, damage: number | null): HitKind {
  const a = action.toLowerCase().replace(/[^a-z]/g, '');
  if (a.includes('critical') || a === 'crit') return 'crit';
  if (a === 'missed' || a === 'miss') return 'miss';
  if (a === 'hit' || a === 'won') return damage ? 'hit' : a === 'hit' ? 'hit' : 'other';
  if (a) return damage ? 'hit' : 'other';
  if (/\bmiss(?:ed|ing)\b/i.test(text)) return 'miss';
  if (/\bcritical\b/i.test(text) && damage) return 'crit';
  return damage ? 'hit' : 'other';
}

const zero = (): LookupTotals => ({ hits: 0, crits: 0, misses: 0, damage: 0 });

/** Weapons and totals from the hits (both worked out the same way for every source). */
function tally(hits: readonly LookupHit[]): Pick<AttackLookup, 'weapons' | 'totals'> {
  const totals: Record<FightSideName, LookupTotals> = { attacker: zero(), defender: zero() };
  const weapons = new Map<string, LookupWeapon>();
  for (const h of hits) {
    if (h.kind === 'other' && !h.bonuses.length) continue;
    const t = h.side ? totals[h.side] : null;
    if (t && h.kind !== 'other') {
      if (h.kind === 'miss') t.misses++;
      else {
        t.hits++;
        if (h.kind === 'crit') t.crits++;
        t.damage += h.damage ?? 0;
      }
    }
    if (!h.weapon) continue;
    const k = `${h.side}|${h.weapon}`;
    const w = weapons.get(k) ?? { side: h.side, name: h.weapon, ammo: [], bonuses: [], hits: 0, crits: 0, misses: 0, damage: 0 };
    if (h.kind === 'miss') w.misses++;
    else if (h.kind !== 'other') {
      w.hits++;
      if (h.kind === 'crit') w.crits++;
      w.damage += h.damage ?? 0;
    }
    if (h.ammo && !w.ammo.includes(h.ammo)) w.ammo.push(h.ammo);
    for (const b of h.bonuses) if (!w.bonuses.includes(b)) w.bonuses.push(b);
    weapons.set(k, w);
  }
  return { weapons: [...weapons.values()].sort((a, b) => (a.side === b.side ? b.damage - a.damage : a.side === 'attacker' ? -1 : 1)), totals };
}

const player = (id: unknown, name: unknown): LookupPlayer | null => {
  const i = posId(id);
  const n = str(name, 40);
  return i || n ? { id: i, name: n ?? `Player ${i}` } : null;
};

/**
 * Torn's v2 API attack log (`/torn/attacklog?log=<code>`: { attacklog: { log: [{ text, timestamp, action, icon, attacker:
 * { id, name, item: { id, name } } | null, defender: { id, name } | null, attacker_item? }], summary: [{ id, name, hits,
 * misses, damage }] } }) → the fight; null when Torn's answer holds no lines.
 */
export function lookupFromApi(body: unknown, code: string): AttackLookup | null {
  const al = obj(obj(body)?.attacklog);
  const raw = Array.isArray(al?.log) ? (al!.log as unknown[]).map(obj).filter((x): x is Obj => !!x) : [];
  if (!raw.length) return null;
  // Oldest first (Torn may answer newest first): the order within a second is kept.
  const lines = (num(raw[0]!.timestamp) ?? 0) > (num(raw[raw.length - 1]!.timestamp) ?? 0) ? [...raw].reverse() : raw;
  const opener = lines.find((l) => /start|initiat/i.test(String(l.action ?? '')) || /initiated an attack/i.test(String(l.text ?? ''))) ?? lines[0]!;
  let attacker = player(obj(opener.attacker)?.id, obj(opener.attacker)?.name);
  let defender = player(obj(opener.defender)?.id, obj(opener.defender)?.name);
  if (!defender) for (const l of lines) defender ??= player(obj(l.defender)?.id, obj(l.defender)?.name);
  if (!attacker) attacker = { id: null, name: 'Someone (stealthed)' };
  const sideOf = (l: Obj): FightSideName | null => {
    const a = posId(obj(l.attacker)?.id);
    const d = posId(obj(l.defender)?.id);
    if (a !== null && a === attacker!.id) return 'attacker';
    if (a !== null && defender && a === defender.id) return 'defender';
    if (d !== null && defender && d === defender.id) return 'attacker';
    if (d !== null && d === attacker!.id) return 'defender';
    return null;
  };
  const hits: LookupHit[] = lines.map((l) => {
    const text = str(l.text, 300) ?? '';
    const dm = DAMAGE.exec(text);
    const damage = dm ? Number(dm[1]!.replace(/,/g, '')) || null : null;
    const fired = FIRED.exec(text);
    const item = str(obj(obj(l.attacker)?.item)?.name, 60) ?? str(obj(l.attacker_item)?.name, 60);
    const weapon = item ?? (fired ? str(fired[3], 60) : str(MELEE.exec(text)?.[1], 60));
    const kind = kindOf(String(l.action ?? ''), text, damage);
    return {
      at: num(l.timestamp),
      side: sideOf(l),
      kind,
      weapon: kind === 'other' && !item ? null : weapon,
      ammo: fired?.[2] ? str(fired[2], 40) : null,
      rounds: fired ? Number(fired[1]) : null,
      zone: kind === 'hit' || kind === 'crit' ? str(ZONE.exec(text)?.[1], 30) : null,
      damage: kind === 'miss' ? null : damage,
      bonuses: bonusesIn(text),
      text,
    };
  });
  const out: AttackLookup = { code, attackId: null, sources: ['api'], attacker, defender, started: hits[0]?.at ?? null, ended: hits[hits.length - 1]?.at ?? null, result: null, hits, ...tally(hits) };
  // Torn's own count per player, where it gives one (it knows misses the lines don't spell out).
  const summary = Array.isArray(al?.summary) ? (al!.summary as unknown[]).map(obj).filter((x): x is Obj => !!x) : [];
  for (const s of summary) {
    const id = posId(s.id);
    const side: FightSideName | null = id !== null && id === attacker.id ? 'attacker' : id !== null && id === defender?.id ? 'defender' : !id && attacker.id === null ? 'attacker' : null;
    if (!side) continue;
    const t = out.totals[side];
    t.hits = num(s.hits) ?? t.hits;
    t.misses = num(s.misses) ?? t.misses;
    t.damage = num(s.damage) ?? t.damage;
  }
  const last = [...lines].reverse().find((l) => /^(hosp|mug|won|lost|stalemate|runaway|leave|timeout|escapefail|attackerhosp|opponenthosp)/i.test(String(l.action ?? '').replace(/[^a-z]/gi, '')));
  if (last) out.result = RESULT_WORD[String(last.action).toLowerCase().replace(/[^a-z]/g, '')] ?? str(last.action, 30);
  return out;
}

const RESULT_WORD: Record<string, string> = { hosp: 'Hospitalized', opponenthosp: 'Hospitalized', mug: 'Mugged', leave: 'Attacked', won: 'Won', lost: 'Lost', stalemate: 'Stalemate', runaway: 'Escape', escapefail: 'Escape failed', timeout: 'Timeout', attackerhosp: 'Lost' };

/** The viewer as one of the two players (by id), or null. */
export function viewerSide(f: Pick<AttackLookup, 'attacker' | 'defender'>, viewer: number): FightSideName | null {
  if (f.attacker?.id === viewer) return 'attacker';
  if (f.defender?.id === viewer) return 'defender';
  return null;
}

/**
 * The viewer's own kept fight (Torn's v1 `attacks` entry their key read — background tracking, `/api/me/attacks`): both
 * players, times, the result — no hits (Torn's list doesn't have them).
 */
export function lookupFromYourFight(e: unknown, id: string | number): AttackLookup | null {
  const a = obj(e);
  if (!a) return null;
  const stealthed = a.stealthed === 1 || a.stealthed === true;
  const attacker = stealthed && !posId(a.attacker_id) ? { id: null, name: 'Someone (stealthed)' } : player(a.attacker_id, a.attacker_name);
  const defender = player(a.defender_id, a.defender_name);
  if (!attacker && !defender) return null;
  return {
    code: str(a.code, 64),
    attackId: posId(id),
    sources: ['yours'],
    attacker,
    defender,
    started: num(a.timestamp_started),
    ended: num(a.timestamp_ended),
    result: str(a.result, 30),
    hits: [],
    weapons: [],
    totals: { attacker: zero(), defender: zero() },
  };
}

/**
 * A fight the player database holds (its 'fight' record from a fighter's API attack list, or its 'fightlog' from the attack
 * page a fighter was viewing), for a viewer who was one of the two players — only what Torn showed them of that fight:
 * both players (a stealthed attacker stays hidden from the defender), the result and every turn's weapon, ammo, body part,
 * damage and bonus. Never the fair fight, respect, modifiers, life, moves (hit chance, defence…), the loadout, the
 * sender or the lines' own words (names). Null when the viewer wasn't in it.
 */
export function lookupFromDb(kind: 'fight' | 'fightlog', data: unknown, viewer: number): AttackLookup | null {
  const d = obj(data);
  if (!d) return null;
  const side = (x: unknown) => {
    const o = obj(x);
    return o ? player(o.id ?? o.userID, o.name ?? o.playername) : null;
  };
  let attacker = side(d.attacker);
  const defender = side(d.defender);
  if (viewerSide({ attacker, defender }, viewer) === null) return null;
  const turns = kind === 'fightlog' && Array.isArray(d.turns) ? (d.turns as unknown[]).map(obj).filter((x): x is Obj => !!x) : [];
  // Any sign of stealth hides the attacker from the defender: the record's flag, a turn's, or the attack page's stealth
  // level (kept even when the oldest turns were trimmed to fit — core/fight-page.ts fitFightLog).
  const stealthed = d.stealthed === true || d.stealthed === 1 || (num(d.stealthLevel) ?? 0) > 0 || turns.some((t) => t.stealthed === true || t.stealthed === 1);
  if (stealthed && attacker?.id !== viewer) attacker = { id: null, name: 'Someone (stealthed)' };
  const hits: LookupHit[] = turns.map((t) => {
    const result = str(t.result, 30) ?? '';
    const damage = num(t.damage);
    const words = `${result} ${str(t.special, 120) ?? ''} ${str(t.text, 300) ?? ''}`;
    const kind = kindOf(/crit/i.test(words) && damage ? 'critical' : /miss/i.test(result) ? 'missed' : '', words, damage && damage > 0 ? damage : null);
    const fired = FIRED.exec(str(t.text, 300) ?? '');
    const weapon = str(t.weapon, 60);
    const zone = str(t.zone, 30);
    const s: FightSideName | null = t.side === 'attacker' || t.side === 'defender' ? t.side : null;
    const what = kind === 'miss' ? 'missed' : kind === 'crit' ? 'critical hit' : kind === 'hit' ? 'hit' : result || 'turn';
    return {
      at: num(t.at),
      side: s,
      kind,
      weapon: kind === 'other' ? null : weapon,
      ammo: fired?.[2] ? str(fired[2], 40) : null,
      rounds: fired ? Number(fired[1]) : null,
      zone: kind === 'hit' || kind === 'crit' ? zone : null,
      damage: kind === 'miss' || kind === 'other' ? null : damage,
      bonuses: bonusesIn(words),
      text: [s === 'attacker' ? 'Attacker' : s === 'defender' ? 'Defender' : '', what, weapon && kind !== 'other' ? `with ${weapon}` : '', zone && kind !== 'miss' && kind !== 'other' ? `in the ${zone}` : '', damage && kind !== 'miss' && kind !== 'other' ? `for ${damage.toLocaleString('en-US')}` : ''].filter(Boolean).join(' '),
    };
  });
  return {
    code: str(d.code, 64) ?? str(d.logId, 64),
    attackId: posId(d.attackId) ?? posId(d.fightId),
    sources: ['db'],
    attacker,
    defender,
    started: num(d.startedAt),
    ended: num(d.endedAt),
    result: kind === 'fight' ? str(d.tornResult, 30) : null,
    hits,
    ...tally(hits),
  };
}

/**
 * Several sources' views of one fight → one: the one with the most hits leads; players, times, result and the fight's
 * code and id are filled from the others. A stealthed attacker hidden by any source stays hidden unless `viewer` is that
 * attacker. Null for none.
 */
export function mergeLookups(list: ReadonlyArray<AttackLookup | null>, viewer: number | null = null): AttackLookup | null {
  const all = list.filter((x): x is AttackLookup => !!x);
  if (!all.length) return null;
  const [lead, ...rest] = [...all].sort((a, b) => b.hits.length - a.hits.length);
  const out: AttackLookup = { ...lead!, sources: [...lead!.sources] };
  for (const o of rest) {
    // A hidden (stealthed) attacker is never named from another source — unless that source names the viewer
    // themself as the attacker (their own fight).
    if (!out.attacker) out.attacker = o.attacker;
    else if (out.attacker.id === null && viewer !== null && o.attacker?.id === viewer) out.attacker = o.attacker;
    out.defender ??= o.defender;
    out.code ??= o.code;
    out.attackId ??= o.attackId;
    out.started ??= o.started;
    out.ended ??= o.ended;
    out.result ??= o.result;
    for (const s of o.sources) if (!out.sources.includes(s)) out.sources.push(s);
  }
  // Hidden in any source → hidden, unless the viewer is the attacker.
  if (all.some((x) => x.attacker?.id === null) && out.attacker?.id !== viewer) out.attacker = { id: null, name: 'Someone (stealthed)' };
  return out;
}
